import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { apiFetch } from '../api/client';
import { stageLabel, timeAgo, pad2 } from '../lib/format';
import styles from './TemplatesPage.module.css';

interface Template {
  id: string;
  name: string;
  sector: string | null;
  stage: string | null;
  subjectTemplate: string | null;
  bodyTemplate: string | null;
  variables: string[] | string | null;
  updatedAt: string | null;
  createdAt: string | null;
}

function normalizeVars(raw: string[] | string | null | undefined): string[] {
  if (!raw) return [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  if (Array.isArray(raw)) return raw;
  return [];
}

interface UiState {
  loading: boolean;
  error: string | null;
  message: { text: string; type: 'success' | 'error' } | null;
  search: string;
  debouncedSearch: string;
  sectorFilter: string;
  stageFilter: string;
}

interface FormState {
  id: string;
  name: string;
  sector: string;
  stage: string;
  subject: string;
  body: string;
}

interface ModalsState {
  editorOpen: boolean;
  editorSaving: boolean;
  deleteOpen: boolean;
  deleteTargetName: string;
  deleteDeleting: boolean;
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                      */
/* ------------------------------------------------------------------ */

interface EditorModalProps {
  open: boolean;
  form: FormState;
  saving: boolean;
  modalVars: string[];
  onClose: () => void;
  onSave: () => void;
  onChange: (field: keyof FormState, value: string) => void;
}

function EditorModal({ open, form, saving, modalVars, onClose, onSave, onChange }: EditorModalProps) {
  if (!open) return null;
  return (
    <div
      className={styles.tplModalOverlay}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className={styles.tplModal} role="dialog" aria-modal="true" aria-labelledby="tpl-editor-title">
        <p className={styles.tplModalEyebrow}>Plantilla</p>
        <h2 className={styles.tplModalTitle} id="tpl-editor-title">
          {form.id ? 'Editar plantilla' : 'Nueva plantilla'}
        </h2>
        <p className={styles.tplModalNote}>
          Usa <code className={styles.tplInlineCode}>{'{{companyName}}'}</code>,{' '}
          <code className={styles.tplInlineCode}>{'{{contactName}}'}</code>,{' '}
          <code className={styles.tplInlineCode}>{'{{sector}}'}</code>,{' '}
          <code className={styles.tplInlineCode}>{'{{painPoint}}'}</code> como variables. Se detectan automáticamente.
        </p>

        <div className={styles.tplModalField}>
          <label htmlFor="tpl-name">Nombre</label>
          <input
            id="tpl-name"
            type="text"
            placeholder="Ej: Outreach Conservas Inicial"
            value={form.name}
            onChange={(e) => onChange('name', e.target.value)}
          />
        </div>

        <div className={styles.tplModalGrid}>
          <div className={styles.tplModalField}>
            <label htmlFor="tpl-sector">Sector</label>
            <input
              id="tpl-sector"
              type="text"
              placeholder="Conservas, vino… (vacío = genérico)"
              value={form.sector}
              onChange={(e) => onChange('sector', e.target.value)}
            />
          </div>
          <div className={styles.tplModalField}>
            <label htmlFor="tpl-stage">Stage</label>
            <select
              id="tpl-stage"
              value={form.stage}
              onChange={(e) => onChange('stage', e.target.value)}
            >
              <option value="">Cualquiera (genérico)</option>
              <option value="new">Nuevo</option>
              <option value="researching">Investigando</option>
              <option value="outreach">Outreach</option>
              <option value="conversation">Conversación</option>
              <option value="diagnostic">Diagnóstico</option>
              <option value="proposal">Propuesta</option>
              <option value="negotiation">Negociación</option>
            </select>
          </div>
        </div>

        <div className={styles.tplModalField}>
          <label htmlFor="tpl-subject">Asunto</label>
          <input
            id="tpl-subject"
            type="text"
            placeholder="Asunto del email"
            value={form.subject}
            onChange={(e) => onChange('subject', e.target.value)}
          />
        </div>

        <div className={styles.tplModalField}>
          <label htmlFor="tpl-body">Cuerpo</label>
          <textarea
            id="tpl-body"
            rows={9}
            placeholder="Cuerpo del mensaje…"
            value={form.body}
            onChange={(e) => onChange('body', e.target.value)}
          />
        </div>

        <label className={styles.tplModalField}>
          Variables detectadas
          <div className={styles.tplModalVars}>
            {modalVars.length === 0 ? (
              <span className={styles.tplModalVarsEmpty}>
                Aparecerán aquí al escribir{' '}
                <code className={styles.tplInlineCode}>{'{{variables}}'}</code>.
              </span>
            ) : (
              modalVars.map((v) => (
                <span key={v} className={styles.tplVar}>
                  {'{{' + v + '}}'}
                </span>
              ))
            )}
          </div>
        </label>

        <div className={styles.tplModalActions}>
          <button
            type="button"
            className={`${styles.tplBtn} ${styles.tplBtnGhost}`}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.tplBtn}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface DeleteModalProps {
  open: boolean;
  targetName: string;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function DeleteModal({ open, targetName, deleting, onClose, onConfirm }: DeleteModalProps) {
  if (!open) return null;
  return (
    <div
      className={styles.tplModalOverlay}
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className={`${styles.tplModal} ${styles.tplModalTight}`} role="alertdialog" aria-modal="true" aria-labelledby="tpl-delete-title">
        <p className={styles.tplModalEyebrow}>Eliminar</p>
        <h2 className={styles.tplModalTitle} id="tpl-delete-title">
          Retirar plantilla
        </h2>
        <p className={styles.tplModalNote}>
          Vas a eliminar{' '}
          <em className={styles.tplModalEmph}>{targetName}</em>. Esta acción no se puede deshacer.
        </p>
        <div className={styles.tplModalActions}>
          <button
            type="button"
            className={`${styles.tplBtn} ${styles.tplBtnGhost}`}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={`${styles.tplBtn} ${styles.tplBtnDanger}`}
            onClick={onConfirm}
            disabled={deleting}
          >
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface TemplateListProps {
  loading: boolean;
  error: string | null;
  templates: Template[];
  filtered: Template[];
  onEdit: (id: string) => void;
  onDelete: (id: string, name: string) => void;
}

function TemplateList({ loading, error, templates, filtered, onEdit, onDelete }: TemplateListProps) {
  return (
    <div className={styles.tplRows} role="list" aria-label="Lista de plantillas">
      {loading && templates.length === 0 ? (
        <div className={styles.tplEmpty}>Cargando plantillas…</div>
      ) : error && templates.length === 0 ? (
        <div className={styles.tplEmpty}>{error}</div>
      ) : templates.length === 0 ? (
        <div className={styles.tplEmpty}>
          Aún no hay plantillas. Crea la primera para empezar a outreachear.
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.tplEmpty}>
          Ninguna plantilla coincide con los filtros.
        </div>
      ) : (
        filtered.map((tpl, idx) => {
          const when = tpl.updatedAt || tpl.createdAt ? timeAgo(tpl.updatedAt || tpl.createdAt) : '';
          const vars = normalizeVars(tpl.variables);
          const shown = vars.slice(0, 3);
          return (
            <div key={tpl.id} className={styles.tplRow} role="listitem">
              <span className={styles.tplRowIndex}>{pad2(idx + 1)}</span>
              <div className={styles.tplRowCore}>
                <div className={styles.tplRowName}>
                  {tpl.name || 'Sin nombre'}
                </div>
                <div className={styles.tplRowMeta}>
                  {tpl.sector ? (
                    <span>{tpl.sector}</span>
                  ) : (
                    <span className={styles.tplRowScopeGeneric}>Genérico</span>
                  )}
                  {tpl.stage && (
                    <>
                      <span className={styles.sep}>·</span>
                      <span className={styles.tplRowStageLabel}>
                        {stageLabel(tpl.stage)}
                      </span>
                    </>
                  )}
                  {when && (
                    <>
                      <span className={styles.sep}>·</span>
                      <span>{when}</span>
                    </>
                  )}
                </div>
              </div>
              <div className={styles.tplRowPreview}>
                {tpl.subjectTemplate ? (
                  <div className={styles.tplRowSubject}>{tpl.subjectTemplate}</div>
                ) : (
                  <div className={`${styles.tplRowSubject} ${styles.tplRowSubjectEmpty}`}>
                    Sin asunto
                  </div>
                )}
                {vars.length > 0 && (
                  <div className={styles.tplRowVars}>
                    {shown.map((v) => (
                      <span key={v} className={styles.tplVar}>
                        {'{{' + v + '}}'}
                      </span>
                    ))}
                    {vars.length > 3 && (
                      <span className={`${styles.tplVar} ${styles.tplVarOverflow}`}>
                        +{vars.length - 3}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className={styles.tplRowActions}>
                <button
                  type="button"
                  className={styles.tplRowAction}
                  onClick={() => onEdit(tpl.id)}
                >
                  Editar
                </button>
                <button
                  type="button"
                  className={`${styles.tplRowAction} ${styles.tplRowActionDanger}`}
                  onClick={() => onDelete(tpl.id, tpl.name || '')}
                >
                  Eliminar
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

interface TemplateToolbarProps {
  search: string;
  sectorFilter: string;
  stageFilter: string;
  sectors: string[];
  countText: string;
  onSearchChange: (value: string) => void;
  onSectorChange: (value: string) => void;
  onStageChange: (value: string) => void;
}

function TemplateToolbar({
  search,
  sectorFilter,
  stageFilter,
  sectors,
  countText,
  onSearchChange,
  onSectorChange,
  onStageChange,
}: TemplateToolbarProps) {
  return (
    <div className={styles.tplToolbar}>
      <div className={styles.tplSearchWrap}>
        <span className={styles.tplSearchGlyph} aria-hidden="true">
          {'⚲'}
        </span>
        <input
          type="search"
          className={styles.tplSearch}
          placeholder="Buscar por nombre, asunto, sector…"
          autoComplete="off"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <label className={styles.tplFilter}>
        <span>Sector</span>
        <select
          value={sectorFilter}
          onChange={(e) => onSectorChange(e.target.value)}
        >
          <option value="">Todos</option>
          {sectors.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <label className={styles.tplFilter}>
        <span>Stage</span>
        <select
          value={stageFilter}
          onChange={(e) => onStageChange(e.target.value)}
        >
          <option value="">Todos</option>
          <option value="new">Nuevo</option>
          <option value="researching">Investigando</option>
          <option value="outreach">Outreach</option>
          <option value="conversation">Conversación</option>
          <option value="diagnostic">Diagnóstico</option>
          <option value="proposal">Propuesta</option>
          <option value="negotiation">Negociación</option>
        </select>
      </label>
      <span className={styles.tplCount}>{countText}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                      */
/* ------------------------------------------------------------------ */

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [ui, setUi] = useState<UiState>({
    loading: true,
    error: null,
    message: null,
    search: '',
    debouncedSearch: '',
    sectorFilter: '',
    stageFilter: '',
  });
  const [form, setForm] = useState<FormState>({ id: '', name: '', sector: '', stage: '', subject: '', body: '' });
  const [modals, setModals] = useState<ModalsState>({
    editorOpen: false,
    editorSaving: false,
    deleteOpen: false,
    deleteTargetName: '',
    deleteDeleting: false,
  });

  const deleteTargetIdRef = useRef<string | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setUi((prev) => ({ ...prev, loading: true }));
      const data = (await apiFetch('/api/templates')) as { templates?: Template[] };
      const loaded = Array.isArray(data.templates) ? data.templates : [];
      setTemplates(loaded);
      setUi((prev) => ({ ...prev, error: null }));
    } catch (err) {
      setUi((prev) => ({ ...prev, error: err instanceof Error ? err.message : 'Error al cargar plantillas' }));
    } finally {
      setUi((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    const t = setTimeout(() => setUi((prev) => ({ ...prev, debouncedSearch: prev.search })), 120);
    return () => clearTimeout(t);
  }, [ui.search]);

  useEffect(() => {
    if (!ui.message) return;
    const t = setTimeout(() => setUi((prev) => ({ ...prev, message: null })), 3000);
    return () => clearTimeout(t);
  }, [ui.message]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (modals.deleteOpen) {
        setModals((prev) => ({ ...prev, deleteOpen: false }));
        return;
      }
      if (modals.editorOpen) {
        setModals((prev) => ({ ...prev, editorOpen: false }));
        return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modals.editorOpen, modals.deleteOpen]);

  const sectors = useMemo(() => {
    const seen = new Set<string>();
    templates.forEach((t) => {
      const s = (t.sector || '').trim();
      if (s) seen.add(s);
    });
    return Array.from(seen).sort((a, b) => a.localeCompare(b, 'es'));
  }, [templates]);

  useEffect(() => {
    if (ui.sectorFilter && !sectors.includes(ui.sectorFilter)) {
      setUi((prev) => ({ ...prev, sectorFilter: '' }));
    }
  }, [sectors, ui.sectorFilter]);

  const filtered = useMemo(() => {
    const q = ui.debouncedSearch.toLowerCase().trim();
    let out = templates.filter((t) => {
      if (q) {
        const hay = `${t.name || ''} ${t.subjectTemplate || ''} ${t.sector || ''} ${t.bodyTemplate || ''}`;
        if (!hay.toLowerCase().includes(q)) return false;
      }
      if (ui.sectorFilter && t.sector !== ui.sectorFilter) return false;
      if (ui.stageFilter && t.stage !== ui.stageFilter) return false;
      return true;
    });
    out.sort((a, b) => {
      const ua = a.updatedAt ? Date.parse(a.updatedAt) : 0;
      const ub = b.updatedAt ? Date.parse(b.updatedAt) : 0;
      return ub - ua;
    });
    return out;
  }, [templates, ui.debouncedSearch, ui.sectorFilter, ui.stageFilter]);

  const modalVars = useMemo(() => {
    const combined = (form.subject || '') + ' ' + (form.body || '');
    const regex = /\{\{(\w+)\}\}/g;
    const vars = new Set<string>();
    let match: RegExpExecArray | null;
    while ((match = regex.exec(combined)) !== null) {
      vars.add(match[1]);
    }
    return Array.from(vars).sort();
  }, [form.subject, form.body]);

  function openNew() {
    setForm({ id: '', name: '', sector: '', stage: '', subject: '', body: '' });
    setModals((prev) => ({ ...prev, editorOpen: true }));
  }

  function openEdit(id: string) {
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    setForm({
      id: tpl.id,
      name: tpl.name || '',
      sector: tpl.sector || '',
      stage: tpl.stage || '',
      subject: tpl.subjectTemplate || '',
      body: tpl.bodyTemplate || '',
    });
    setModals((prev) => ({ ...prev, editorOpen: true }));
  }

  const handleFormChange = useCallback((field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  async function handleSave() {
    const name = form.name.trim();
    const sector = form.sector.trim();
    const stage = form.stage;
    const subject = form.subject.trim();
    const body = form.body.trim();

    if (!name) {
      setUi((prev) => ({ ...prev, message: { text: 'El nombre es obligatorio', type: 'error' } }));
      return;
    }
    if (!subject) {
      setUi((prev) => ({ ...prev, message: { text: 'El asunto es obligatorio', type: 'error' } }));
      return;
    }
    if (!body) {
      setUi((prev) => ({ ...prev, message: { text: 'El cuerpo es obligatorio', type: 'error' } }));
      return;
    }

    const combined = subject + ' ' + body;
    const regex = /\{\{(\w+)\}\}/g;
    const vars = new Set<string>();
    let match: RegExpExecArray | null;
    while ((match = regex.exec(combined)) !== null) {
      vars.add(match[1]);
    }
    const variables = Array.from(vars).sort();

    const payload = {
      name,
      sector: sector || null,
      stage: stage || null,
      subjectTemplate: subject,
      bodyTemplate: body,
      variables: variables.length ? variables : null,
    };

    setModals((prev) => ({ ...prev, editorSaving: true }));
    try {
      if (form.id) {
        await apiFetch(`/api/templates/${form.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
        setUi((prev) => ({ ...prev, message: { text: 'Plantilla actualizada', type: 'success' } }));
      } else {
        await apiFetch('/api/templates', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setUi((prev) => ({ ...prev, message: { text: 'Plantilla creada', type: 'success' } }));
      }
      setModals((prev) => ({ ...prev, editorOpen: false }));
      loadTemplates();
    } catch (err) {
      setUi((prev) => ({
        ...prev,
        message: {
          text: err instanceof Error ? err.message : 'Error al guardar plantilla',
          type: 'error',
        },
      }));
    } finally {
      setModals((prev) => ({ ...prev, editorSaving: false }));
    }
  }

  function promptDelete(id: string, name: string) {
    deleteTargetIdRef.current = id;
    setModals((prev) => ({ ...prev, deleteTargetName: name || 'esta plantilla', deleteOpen: true }));
  }

  async function confirmDelete() {
    if (!deleteTargetIdRef.current) return;
    setModals((prev) => ({ ...prev, deleteDeleting: true }));
    try {
      await apiFetch(`/api/templates/${deleteTargetIdRef.current}`, { method: 'DELETE' });
      setUi((prev) => ({ ...prev, message: { text: 'Plantilla eliminada', type: 'success' } }));
      setModals((prev) => ({ ...prev, deleteOpen: false }));
      loadTemplates();
    } catch (err) {
      setUi((prev) => ({
        ...prev,
        message: {
          text: err instanceof Error ? err.message : 'Error al eliminar plantilla',
          type: 'error',
        },
      }));
    } finally {
      setModals((prev) => ({ ...prev, deleteDeleting: false }));
      deleteTargetIdRef.current = null;
    }
  }

  return (
    <section className={styles.tplShell} aria-label="Plantillas de outreach">
      <header className={styles.tplMasthead}>
        <div className={styles.tplMastheadInner}>
          <p className={styles.tplKicker}>Plantillas</p>
          <h1 className={styles.tplHead}>Sistema de outreach</h1>
          <p className={styles.tplDeck}>
            Mensajes parametrizables con variables dinámicas. Se cargan al generar outreach desde Operar.
          </p>
        </div>
        <div className={styles.tplActions}>
          <button type="button" className={styles.tplBtn} onClick={openNew}>
            Nueva plantilla
          </button>
        </div>
      </header>

      {ui.message && (
        <div
          className={
            ui.message.type === 'success' ? styles.toastSuccess : styles.toastError
          }
        >
          {ui.message.text}
        </div>
      )}

      <TemplateToolbar
        search={ui.search}
        sectorFilter={ui.sectorFilter}
        stageFilter={ui.stageFilter}
        sectors={sectors}
        countText={`${filtered.length} de ${templates.length}`}
        onSearchChange={(value) => setUi((prev) => ({ ...prev, search: value }))}
        onSectorChange={(value) => setUi((prev) => ({ ...prev, sectorFilter: value }))}
        onStageChange={(value) => setUi((prev) => ({ ...prev, stageFilter: value }))}
      />

      <TemplateList
        loading={ui.loading}
        error={ui.error}
        templates={templates}
        filtered={filtered}
        onEdit={openEdit}
        onDelete={promptDelete}
      />

      <EditorModal
        open={modals.editorOpen}
        form={form}
        saving={modals.editorSaving}
        modalVars={modalVars}
        onClose={() => setModals((prev) => ({ ...prev, editorOpen: false }))}
        onSave={handleSave}
        onChange={handleFormChange}
      />

      <DeleteModal
        open={modals.deleteOpen}
        targetName={modals.deleteTargetName}
        deleting={modals.deleteDeleting}
        onClose={() => setModals((prev) => ({ ...prev, deleteOpen: false }))}
        onConfirm={confirmDelete}
      />
    </section>
  );
}
